import { useState, useEffect } from 'react';
import { useCurrencies, FALLBACK_CURRENCIES } from '../context/CurrencyContext';

const NAVY = '#002E53';
const GOLD = '#C5A059';
const FONT = "'Poppins', sans-serif";

export default function AdminSettingsPanel({ token }) {
  const { fetchCurrencies } = useCurrencies();
  const [currencies, setCurrencies] = useState([]);
  const [autoUpdateCurrency, setAutoUpdateCurrency] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [updatingAuto, setUpdatingAuto] = useState(false);
  const [toast, setToast] = useState(null);
  
  const [passwords, setPasswords] = useState({ current: '', new: '' });
  const [changingPass, setChangingPass] = useState(false);

  const [admins, setAdmins] = useState([]);
  const [newAdmin, setNewAdmin] = useState({ email: '', password: '' });
  const [addingAdmin, setAddingAdmin] = useState(false);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const res = await fetch('/api/settings');
        if (res.ok) {
          const data = await res.json();
          setCurrencies(data.currencies || FALLBACK_CURRENCIES);
          setAutoUpdateCurrency(data.autoUpdateCurrency || false);
        } else {
          setCurrencies(FALLBACK_CURRENCIES);
        }
      } catch (err) {
        console.error(err);
        setCurrencies(FALLBACK_CURRENCIES);
      } finally {
        setLoading(false);
      }
    };
    
    const fetchAdmins = async () => {
      try {
        const res = await fetch('/api/admin/users', { headers: { 'x-admin-token': token } });
        if (res.ok) setAdmins(await res.json());
      } catch (err) {}
    };

    loadSettings();
    fetchAdmins();
  }, [token]);

  const handleRateChange = (code, val) => {
    setCurrencies(prev => prev.map(c => c.code === code ? { ...c, rate: Number(val) } : c));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch('/api/settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-admin-token': token },
        body: JSON.stringify({ currencies, autoUpdateCurrency })
      });
      if (!res.ok) throw new Error('Failed to save settings');
      showToast('Settings saved successfully');
      fetchCurrencies(); // Update global context in frontend
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleAutoUpdate = async () => {
    setUpdatingAuto(true);
    try {
      const res = await fetch('/api/settings/auto-update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-admin-token': token }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to update rates');
      
      setCurrencies(data.settings.currencies);
      showToast(data.message || 'Exchange rates synced successfully!');
      fetchCurrencies();
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setUpdatingAuto(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setChangingPass(true);
    try {
      const res = await fetch('/api/admin/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-admin-token': token },
        body: JSON.stringify({ currentPassword: passwords.current, newPassword: passwords.new })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to change password');
      showToast('Password changed successfully');
      setPasswords({ current: '', new: '' });
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setChangingPass(false);
    }
  };

  const handleAddAdmin = async (e) => {
    e.preventDefault();
    setAddingAdmin(true);
    try {
      const res = await fetch('/api/admin/users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-admin-token': token },
        body: JSON.stringify({ email: newAdmin.email, password: newAdmin.password })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to add admin');
      showToast('Administrator added successfully');
      setNewAdmin({ email: '', password: '' });
      setAdmins(prev => [...prev, data.admin]);
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setAddingAdmin(false);
    }
  };

  const handleDeleteAdmin = async (id) => {
    if (!window.confirm('Are you sure you want to delete this administrator?')) return;
    try {
      const res = await fetch(`/api/admin/users/${id}`, {
        method: 'DELETE',
        headers: { 'x-admin-token': token }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to delete admin');
      showToast('Administrator deleted');
      setAdmins(prev => prev.filter(a => a.id !== id));
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
        <div style={{ width: '40px', height: '40px', border: `3px solid ${GOLD}30`, borderTopColor: GOLD, borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
      </div>
    );
  }

  return (
    <div style={{ padding: 'clamp(16px,3vw,28px)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h3 style={{ fontFamily: FONT, fontSize: '18px', fontWeight: 700, color: NAVY, margin: '0 0 4px' }}>System Settings</h3>
          <p style={{ fontFamily: FONT, fontSize: '13px', color: '#64748b', margin: 0 }}>Manage exchange rates and global configurations.</p>
        </div>
      </div>

      <div style={{ background: '#fff', borderRadius: '14px', padding: '24px', boxShadow: '0 2px 8px rgba(0,46,83,0.05)', marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px', marginBottom: '20px' }}>
          <div>
            <h4 style={{ fontFamily: FONT, fontSize: '16px', fontWeight: 700, color: NAVY, margin: '0 0 4px' }}>Currency Exchange Rates</h4>
            <p style={{ fontFamily: FONT, fontSize: '13px', color: '#64748b', margin: 0 }}>Rates are relative to 1 AED (Base Currency).</p>
          </div>
          <button 
            onClick={handleAutoUpdate} 
            disabled={updatingAuto}
            style={{ 
              display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 18px', borderRadius: '8px', 
              border: `1px solid ${updatingAuto ? '#e2e8f0' : GOLD}`, 
              background: updatingAuto ? '#f8fafc' : `${GOLD}10`, 
              color: updatingAuto ? '#94a3b8' : NAVY, 
              fontFamily: FONT, fontSize: '13px', fontWeight: 600, cursor: updatingAuto ? 'not-allowed' : 'pointer', transition: 'all 0.15s' 
            }}
          >
            {updatingAuto ? (
              <span style={{ width: '14px', height: '14px', border: `2px solid #cbd5e1`, borderTopColor: '#94a3b8', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
            ) : '✨'} 
            {updatingAuto ? 'Syncing Rates...' : 'Sync via Gemini AI'}
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '16px' }}>
          {currencies.map(c => (
            <div key={c.code} style={{ background: '#f8fafb', border: '1px solid #eaeef3', borderRadius: '10px', padding: '14px 18px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                <span style={{ fontSize: '20px' }}>{c.flag}</span>
                <span style={{ fontFamily: FONT, fontSize: '14px', fontWeight: 700, color: NAVY }}>{c.code}</span>
                <span style={{ fontFamily: FONT, fontSize: '11px', color: '#94a3b8', marginLeft: 'auto' }}>{c.name}</span>
              </div>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontFamily: FONT, fontSize: '12px', fontWeight: 600, color: '#64748b' }}>Rate:</span>
                {c.code === 'AED' ? (
                  <input type="number" value={1} disabled style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', background: '#f1f5f9', color: '#94a3b8', fontFamily: FONT, fontSize: '13px', outline: 'none' }} />
                ) : (
                  <input 
                    type="number" 
                    step="0.001"
                    value={c.rate} 
                    onChange={(e) => handleRateChange(c.code, e.target.value)}
                    style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontFamily: FONT, fontSize: '13px', color: NAVY, outline: 'none', transition: 'border-color 0.2s' }} 
                  />
                )}
              </div>
            </div>
          ))}
        </div>

        <div style={{ marginTop: '24px', paddingTop: '20px', borderTop: '1px solid #eaeef3', display: 'flex', justifyContent: 'flex-end' }}>
          <button 
            onClick={handleSave} 
            disabled={saving}
            style={{ 
              padding: '12px 28px', borderRadius: '10px', border: 'none', 
              background: saving ? '#cbd5e1' : NAVY, color: '#fff', 
              fontFamily: FONT, fontSize: '14px', fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', 
              boxShadow: saving ? 'none' : '0 4px 12px rgba(0,46,83,0.15)', transition: 'all 0.2s' 
            }}
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px', marginBottom: '24px' }}>
        <div style={{ background: '#fff', borderRadius: '14px', padding: '24px', boxShadow: '0 2px 8px rgba(0,46,83,0.05)' }}>
          <h4 style={{ fontFamily: FONT, fontSize: '16px', fontWeight: 700, color: NAVY, margin: '0 0 16px' }}>Manage Administrators</h4>
          
          <div style={{ marginBottom: '24px', border: '1px solid #eaeef3', borderRadius: '10px', overflow: 'hidden' }}>
            {admins.map((admin, idx) => (
              <div key={admin.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: idx % 2 === 0 ? '#f8fafb' : '#fff', borderBottom: idx === admins.length - 1 ? 'none' : '1px solid #eaeef3' }}>
                <div>
                  <div style={{ fontFamily: FONT, fontSize: '14px', fontWeight: 600, color: NAVY }}>{admin.email}</div>
                  <div style={{ fontFamily: FONT, fontSize: '11px', color: '#64748b' }}>Added: {new Date(admin.createdAt).toLocaleDateString()}</div>
                </div>
                <button 
                  onClick={() => handleDeleteAdmin(admin.id)}
                  style={{ background: 'transparent', border: '1px solid #fca5a5', color: '#dc2626', padding: '6px 12px', borderRadius: '6px', fontFamily: FONT, fontSize: '12px', fontWeight: 600, cursor: 'pointer', transition: 'background 0.2s' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#fef2f2'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>

          <form onSubmit={handleAddAdmin}>
            <h5 style={{ fontFamily: FONT, fontSize: '13px', fontWeight: 700, color: NAVY, marginBottom: '12px' }}>Add New Admin</h5>
            <div style={{ marginBottom: '12px' }}>
              <input 
                type="email" 
                placeholder="Email Address"
                value={newAdmin.email}
                onChange={e => setNewAdmin({ ...newAdmin, email: e.target.value })}
                style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontFamily: FONT, fontSize: '14px', outline: 'none' }}
                required
              />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <input 
                type="password" 
                placeholder="Password (min 6 chars)"
                value={newAdmin.password}
                onChange={e => setNewAdmin({ ...newAdmin, password: e.target.value })}
                style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontFamily: FONT, fontSize: '14px', outline: 'none' }}
                required
                minLength={6}
              />
            </div>
            <button 
              type="submit"
              disabled={addingAdmin}
              style={{ 
                width: '100%', padding: '10px 20px', borderRadius: '8px', border: 'none', 
                background: addingAdmin ? '#cbd5e1' : `${GOLD}20`, color: addingAdmin ? '#fff' : NAVY, 
                fontFamily: FONT, fontSize: '13px', fontWeight: 700, cursor: addingAdmin ? 'not-allowed' : 'pointer',
              }}
            >
              {addingAdmin ? 'Adding...' : '+ Add Administrator'}
            </button>
          </form>
        </div>

        <div style={{ background: '#fff', borderRadius: '14px', padding: '24px', boxShadow: '0 2px 8px rgba(0,46,83,0.05)' }}>
          <h4 style={{ fontFamily: FONT, fontSize: '16px', fontWeight: 700, color: NAVY, margin: '0 0 16px' }}>My Security Settings</h4>
        
        <form onSubmit={handlePasswordChange} style={{ maxWidth: '400px' }}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontFamily: FONT, fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '8px' }}>Current Password</label>
            <input 
              type="password" 
              value={passwords.current}
              onChange={e => setPasswords({ ...passwords, current: e.target.value })}
              style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontFamily: FONT, fontSize: '14px', outline: 'none' }}
              required
            />
          </div>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontFamily: FONT, fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '8px' }}>New Password</label>
            <input 
              type="password" 
              value={passwords.new}
              onChange={e => setPasswords({ ...passwords, new: e.target.value })}
              style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontFamily: FONT, fontSize: '14px', outline: 'none' }}
              required
              minLength={6}
            />
          </div>
          <button 
            type="submit"
            disabled={changingPass}
            style={{ 
              padding: '10px 20px', borderRadius: '8px', border: 'none', 
              background: changingPass ? '#cbd5e1' : NAVY, color: '#fff', 
              fontFamily: FONT, fontSize: '13px', fontWeight: 600, cursor: changingPass ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s'
            }}
          >
            {changingPass ? 'Updating...' : 'Update Password'}
          </button>
        </form>
      </div>
      </div>

      {toast && (
        <div style={{ position: 'fixed', bottom: '28px', right: '28px', zIndex: 99999, background: toast.type === 'error' ? '#dc2626' : '#10b981', color: '#fff', fontFamily: FONT, fontSize: '13px', fontWeight: 600, padding: '14px 20px', borderRadius: '12px', boxShadow: '0 10px 40px rgba(0,0,0,0.25)', animation: 'toastIn 0.3s ease', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '16px' }}>{toast.type === 'error' ? '⚠️' : '✓'}</span>
          {toast.msg}
        </div>
      )}
    </div>
  );
}
